import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useI18n } from "../context/i18n-context";
import { useDisciplio } from "../context/disciplio-context";
import { BROWSER_STORAGE_KEYS } from "../browser-storage";
import { useBrowserStorageConsent } from "../context/browser-storage-consent-context";
import { AnalyticsPrivate, trackClick } from "../analytics";
import { OnboardingGuide } from "./OnboardingGuide";
import { DailyGoalNotifications } from "./DailyGoalNotifications";
import { NotificationPermissionPrompt } from "./NotificationPermissionPrompt";

const SHELL_ASIDE_COLLAPSED_STORAGE_KEY =
  BROWSER_STORAGE_KEYS.shellAsideCollapsed;
const SHELL_ASIDE_WIDTH_STORAGE_KEY = BROWSER_STORAGE_KEYS.shellAsideWidth;

export const SHELL_ASIDE_MIN_WIDTH = 168;
export const SHELL_ASIDE_MAX_WIDTH = 320;
export const SHELL_ASIDE_DEFAULT_WIDTH = 196;
const SHELL_ASIDE_KEYBOARD_STEP = 16;

function clampAsideWidth(width: number) {
  return Math.min(
    SHELL_ASIDE_MAX_WIDTH,
    Math.max(SHELL_ASIDE_MIN_WIDTH, Math.round(width))
  );
}

function readStoredAsideCollapsed(allowOptionalStorage: boolean) {
  if (!allowOptionalStorage || typeof window === "undefined") return true;
  try {
    const stored = window.localStorage.getItem(SHELL_ASIDE_COLLAPSED_STORAGE_KEY);
    return stored == null ? true : stored === "true";
  } catch {
    return true;
  }
}

function storeAsideCollapsed(
  collapsed: boolean,
  allowOptionalStorage: boolean
) {
  if (!allowOptionalStorage || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      SHELL_ASIDE_COLLAPSED_STORAGE_KEY,
      String(collapsed)
    );
  } catch {
    // A navigation preference should never block the app if storage is unavailable.
  }
}

function readStoredAsideWidth(allowOptionalStorage: boolean) {
  if (!allowOptionalStorage || typeof window === "undefined") {
    return SHELL_ASIDE_DEFAULT_WIDTH;
  }
  try {
    const stored = window.localStorage.getItem(SHELL_ASIDE_WIDTH_STORAGE_KEY);
    if (stored == null) return SHELL_ASIDE_DEFAULT_WIDTH;
    const parsed = Number.parseInt(stored, 10);
    return Number.isFinite(parsed)
      ? clampAsideWidth(parsed)
      : SHELL_ASIDE_DEFAULT_WIDTH;
  } catch {
    return SHELL_ASIDE_DEFAULT_WIDTH;
  }
}

function storeAsideWidth(width: number, allowOptionalStorage: boolean) {
  if (!allowOptionalStorage || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SHELL_ASIDE_WIDTH_STORAGE_KEY, String(width));
  } catch {
    // A navigation preference should never block the app if storage is unavailable.
  }
}

function NavIcon({ kind }: { kind: "today" | "week" | "calendar" | "planning" | "insights" | "coach" | "account" }) {
  if (kind === "today") {
    return (
      <svg viewBox="0 0 20 20" className="shell-nav__icon" aria-hidden="true">
        <path d="M4 4h12v12H4z" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M7 2.8v2.4M13 2.8v2.4M4 7h12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "week") {
    return (
      <svg viewBox="0 0 20 20" className="shell-nav__icon" aria-hidden="true">
        <path d="M3.5 5h13M3.5 10h13M3.5 15h13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "calendar") {
    return (
      <svg viewBox="0 0 20 20" className="shell-nav__icon" aria-hidden="true">
        <path d="M4 4h12v12H4zM4 8h12" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M7 2.8v2.4M13 2.8v2.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "planning") {
    return (
      <svg viewBox="0 0 20 20" className="shell-nav__icon" aria-hidden="true">
        <path d="M4 4h5v5H4zM11 4h5v5h-5zM4 11h5v5H4zM11 11h5v5h-5z" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    );
  }
  if (kind === "account") {
    return (
      <svg viewBox="0 0 20 20" className="shell-nav__icon" aria-hidden="true">
        <path d="M10 10.2a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8Z" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M4.2 16.5c.78-2.7 2.76-4.2 5.8-4.2s5.02 1.5 5.8 4.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "coach") {
    return <img src="/assets/dio-mascot.png" className="shell-nav__icon shell-nav__icon--coach" alt="" aria-hidden="true" />;
  }
  return (
    <svg viewBox="0 0 20 20" className="shell-nav__icon" aria-hidden="true">
      <path d="M4 15.5V9.8M10 15.5V6.5M16 15.5V4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function AppShell() {
  const { pathname } = useLocation();
  const { state } = useDisciplio();
  const { t } = useI18n();
  const { isOptionalStorageEnabled } = useBrowserStorageConsent();
  const [isAsideCollapsed, setIsAsideCollapsed] = useState(() =>
    readStoredAsideCollapsed(isOptionalStorageEnabled)
  );
  const [asideWidth, setAsideWidth] = useState(() =>
    readStoredAsideWidth(isOptionalStorageEnabled)
  );
  const [isAsideResizing, setIsAsideResizing] = useState(false);
  const asideResizeStart = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
  } | null>(null);
  const fullWidthPlanningLayout = pathname === "/planning";
  const isAdminRoute = pathname.startsWith("/admin");
  const fullWidthLayout = fullWidthPlanningLayout || isAdminRoute;
  const isAccountRoute = pathname === "/account" || pathname.startsWith("/account/");
  const { profile } = state;
  const initials = profile.fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0]?.toUpperCase() ?? "")
    .join("") || "D";
  const primaryGoal = profile.globalGoals[0];
  const focusPreview =
    primaryGoal ||
    profile.interests.slice(0, 2).join(" · ") ||
    t("app.profileEmpty");
  const pageTitle = useMemo(() => {
    const titles: Record<string, string> = {
      "/": t("app.title.today"),
      "/goals": t("app.title.week"),
      "/calendar": t("app.title.calendar"),
      "/planning": t("app.title.planning"),
      "/insights": t("app.title.insights"),
      "/coach": t("app.title.coach"),
      "/account": t("app.title.account"),
      "/admin/rag": t("app.title.ragAdmin"),
      "/admin/ai-usage": t("app.title.aiUsageAdmin"),
    };
    return titles[pathname] ?? (pathname.startsWith("/account/") ? t("app.title.account") : "Disciplio");
  }, [pathname, t]);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => {
    const routeKey = pathname === "/" ? "today" : pathname.replace(/^\//, "").split("/")[0];
    if (typeof document !== "undefined" && document.body) {
      document.body.dataset.route = routeKey || "today";
    }
  }, [pathname]);

  useEffect(() => {
    storeAsideCollapsed(isAsideCollapsed, isOptionalStorageEnabled);
  }, [isAsideCollapsed, isOptionalStorageEnabled]);

  useEffect(() => {
    storeAsideWidth(asideWidth, isOptionalStorageEnabled);
  }, [asideWidth, isOptionalStorageEnabled]);

  useEffect(() => {
    if (!isAsideResizing || typeof document === "undefined") return;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isAsideResizing]);

  useEffect(() => {
    if (!isAsideResizing || typeof window === "undefined") return;
    const handleWindowPointerMove = (event: PointerEvent) => {
      const start = asideResizeStart.current;
      if (!start || start.pointerId !== event.pointerId) return;
      setAsideWidth(
        clampAsideWidth(start.startWidth + event.clientX - start.startX)
      );
    };
    const handleWindowPointerUp = (event: PointerEvent) => {
      if (asideResizeStart.current?.pointerId !== event.pointerId) return;
      asideResizeStart.current = null;
      setIsAsideResizing(false);
    };
    window.addEventListener("pointermove", handleWindowPointerMove);
    window.addEventListener("pointerup", handleWindowPointerUp);
    window.addEventListener("pointercancel", handleWindowPointerUp);
    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove);
      window.removeEventListener("pointerup", handleWindowPointerUp);
      window.removeEventListener("pointercancel", handleWindowPointerUp);
    };
  }, [isAsideResizing]);

  function handleAsideResizePointerDown(
    event: ReactPointerEvent<HTMLDivElement>
  ) {
    if (event.button !== 0) return;
    asideResizeStart.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: asideWidth,
    };
    setIsAsideResizing(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  function handleAsideResizePointerMove(
    event: ReactPointerEvent<HTMLDivElement>
  ) {
    const start = asideResizeStart.current;
    if (!start || start.pointerId !== event.pointerId) return;
    setAsideWidth(clampAsideWidth(start.startWidth + event.clientX - start.startX));
  }

  function handleAsideResizePointerUp(
    event: ReactPointerEvent<HTMLDivElement>
  ) {
    const start = asideResizeStart.current;
    if (!start || start.pointerId !== event.pointerId) return;
    asideResizeStart.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setIsAsideResizing(false);
  }

  function handleAsideResizeKeyDown(
    event: ReactKeyboardEvent<HTMLDivElement>
  ) {
    let nextWidth: number | null = null;
    if (event.key === "ArrowLeft") {
      nextWidth = asideWidth - SHELL_ASIDE_KEYBOARD_STEP;
    } else if (event.key === "ArrowRight") {
      nextWidth = asideWidth + SHELL_ASIDE_KEYBOARD_STEP;
    } else if (event.key === "Home") {
      nextWidth = SHELL_ASIDE_MIN_WIDTH;
    } else if (event.key === "End") {
      nextWidth = SHELL_ASIDE_MAX_WIDTH;
    }
    if (nextWidth == null) return;
    event.preventDefault();
    setAsideWidth(clampAsideWidth(nextWidth));
  }

  return (
    <div
      className={`shell${fullWidthPlanningLayout ? " shell--planning-mode" : ""}${isAdminRoute ? " shell--admin-mode" : ""}${isAsideCollapsed ? " shell--aside-collapsed" : ""}${isAsideResizing ? " shell--aside-resizing" : ""}`}
      style={{ "--shell-aside-width": `${asideWidth}px` } as CSSProperties}
    >
      <a href="#main" className="skip-link">
        {t("app.skipToMain")}
      </a>
      {!fullWidthLayout && !isAccountRoute ? (
        <Link
          to="/account"
          className="shell-mobile-avatar"
          aria-label={t("app.account")}
        >
          <NavIcon kind="account" />
        </Link>
      ) : null}
      {!fullWidthLayout ? (
        <aside className="shell-aside" aria-label={t("app.site")}>
          <div className="shell-aside__header">
            <button
              type="button"
              className="shell-aside__rail-brand"
              aria-label={t("app.expandNavigation")}
              title={t("app.expandNavigation")}
              onClick={() => setIsAsideCollapsed(false)}
            >
              <img src="/assets/dio-mascot.png" alt="" aria-hidden="true" />
            </button>
            <div className="brand">
              <p className="brand__tag">{t("app.brandTag")}</p>
              <h2 className="brand__mark">Disciplio</h2>
            </div>
            <button
              type="button"
              className="shell-aside__toggle"
              aria-controls="primary-navigation"
              aria-expanded={!isAsideCollapsed}
              aria-label={t(
                isAsideCollapsed
                  ? "app.expandNavigation"
                  : "app.collapseNavigation"
              )}
              title={t(
                isAsideCollapsed
                  ? "app.expandNavigation"
                  : "app.collapseNavigation"
              )}
              onClick={() => setIsAsideCollapsed((collapsed) => !collapsed)}
            >
              <svg
                viewBox="0 0 20 20"
                className="shell-aside__toggle-icon"
                aria-hidden="true"
              >
                <path
                  d="m12 4-6 6 6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="shell-aside__body">
            <nav className="shell-nav" id="primary-navigation">
              <NavLink to="/" end onClick={() => trackClick("navigation_today")} className={({ isActive }) => (isActive ? "active" : undefined)}>
                <NavIcon kind="today" />
                <span className="shell-nav__label">{t("app.today")}</span>
              </NavLink>
              <NavLink to="/goals" onClick={() => trackClick("navigation_goals")} className={({ isActive }) => (isActive ? "active" : undefined)}>
                <NavIcon kind="week" />
                <span className="shell-nav__label">{t("app.week")}</span>
              </NavLink>
              <NavLink to="/calendar" onClick={() => trackClick("navigation_calendar")} className={({ isActive }) => (isActive ? "active" : undefined)}>
                <NavIcon kind="calendar" />
                <span className="shell-nav__label">{t("app.calendar")}</span>
              </NavLink>
              <NavLink to="/planning" onClick={() => trackClick("navigation_planning")} className={({ isActive }) => (isActive ? "active" : undefined)}>
                <NavIcon kind="planning" />
                <span className="shell-nav__label">{t("app.planning")}</span>
              </NavLink>
              <NavLink to="/coach" onClick={() => trackClick("navigation_coach")} className={({ isActive }) => (isActive ? "active" : undefined)}>
                <NavIcon kind="coach" />
                <span className="shell-nav__label">{t("app.coach")}</span>
              </NavLink>
            </nav>
          </div>

          <div className="shell-aside-footer">
            <Link
              to="/account"
              className="account-summary account-summary--footer"
              aria-label={t("app.account")}
            >
              <span className="account-summary__avatar" aria-hidden="true">
                {initials}
              </span>
              <span className="account-summary__body">
                <strong>{t("app.account")}</strong>
                <AnalyticsPrivate as="span" mode="block">
                  {profile.fullName || focusPreview || t("app.accountFallbackName")}
                </AnalyticsPrivate>
              </span>
            </Link>
          </div>
          <div
            className="shell-aside__resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-valuemin={SHELL_ASIDE_MIN_WIDTH}
            aria-valuemax={SHELL_ASIDE_MAX_WIDTH}
            aria-valuenow={asideWidth}
            aria-label={t("app.resizeNavigation")}
            tabIndex={0}
            onKeyDown={handleAsideResizeKeyDown}
            onPointerDown={handleAsideResizePointerDown}
            onPointerMove={handleAsideResizePointerMove}
            onPointerUp={handleAsideResizePointerUp}
            onPointerCancel={handleAsideResizePointerUp}
            onLostPointerCapture={handleAsideResizePointerUp}
          />
        </aside>
      ) : null}
      <main className="shell-main" id="main">
        <div className="shell-main__content">
          <Outlet />
        </div>
      </main>
      <OnboardingGuide />
      <DailyGoalNotifications />
      <NotificationPermissionPrompt />
    </div>
  );
}
